import styled from "styled-components";
import {
  AgencyTags,
  Card,
  CardSiderFlexContent,
  ScrollingTextArea,
} from "@bagel-web/components";
import { useQuery } from "@tanstack/react-query";
import { CommonAPI, CommonContentMessage, CommonFnCallMessage } from "../types";
import { eventStreamQueries } from "../queries";
import FnCallMessage from "../session/message/FnCallMessage";
import { Badge } from "react-bootstrap";
import ContentMessage from "../session/message/ContentMessage";
import LLMEventRating from "../LLMEventRating";

const MomentSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AgentRecentActionCard = ({
  sessionId,
  agentId,
  momentId,
  commonAPI,
}: {
  sessionId: string;
  agentId: string;
  momentId: number | null;
  commonAPI: CommonAPI;
}) => {
  const { data: momentDetail } = useQuery({
    queryKey: eventStreamQueries.getDetail(sessionId, agentId, momentId),
    queryFn: () =>
      momentId !== null
        ? commonAPI.fetchEventStreamDetail(sessionId, agentId, momentId)
        : null,
    staleTime: Infinity,
  });

  const llmResult = momentDetail?.event_wrappers.find(
    (eventWrapper) => eventWrapper.event_type === "service_interaction"
  );

  const llmErrorMessage = (llmResult?.event?.error_message || null) as
    string | null;

  const agentCreation = momentDetail?.event_wrappers.find(
    (eventWrapper) => eventWrapper.event_type === "created_agent"
  );

  const functionCall = momentDetail?.event_wrappers.find(
    (eventWrapper) =>
      eventWrapper.event_type === "game_event" &&
      eventWrapper.event?.message_type === "FnCallMessage"
  );

  const generatedContent = momentDetail?.event_wrappers.find(
    (eventWrapper) =>
      eventWrapper.event_type === "game_event" &&
      eventWrapper.event?.message_type === "ContentMessage" &&
      (eventWrapper.event?.sender === "generate_text" ||
        eventWrapper.event?.sender === "generate_json")
  );

  const functionCallMessage = functionCall?.event as CommonFnCallMessage;
  const generatedContentMessage =
    generatedContent?.event as CommonContentMessage;

  return (
    <Card
      Sider={() => (
        <CardSiderFlexContent $collapsed={false}>
          <h5>Moment Summary</h5>
          {llmErrorMessage && <Badge bg="danger">Error</Badge>}
          {llmResult && !llmErrorMessage && momentId && (
            <LLMEventRating
              sessionId={sessionId}
              agentId={agentId}
              momentId={momentId}
              eventId={llmResult.id}
              isPositive={llmResult.rating?.is_positive}
              rationale={llmResult.rating?.rationale}
              commonAPI={commonAPI}
            />
          )}
        </CardSiderFlexContent>
      )}
    >
      <MomentSummaryContainer>
        <div className="d-flex flex-row gap-2 align-items-start">
          <ScrollingTextArea
            className="flex-grow-1"
            $maxHeight="200px"
            $resize={false}
          >
            {agentCreation && "Agent created"}
            {functionCallMessage && momentDetail && (
              <>
                <FnCallMessage
                  moment={momentDetail}
                  message={functionCallMessage}
                />
              </>
            )}
            {generatedContentMessage && momentDetail && (
              <ContentMessage
                moment={momentDetail}
                message={generatedContentMessage}
              />
            )}
            {llmErrorMessage && <code>{llmErrorMessage}</code>}
          </ScrollingTextArea>
          {functionCallMessage && (
            <AgencyTags tags={functionCallMessage.agency_tags} />
          )}
        </div>
      </MomentSummaryContainer>
    </Card>
  );
};

export default AgentRecentActionCard;
